import { trackCallClick } from "@/lib/ga4";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

export type GenieChannelKey = "call" | "email" | "guides" | "talk_again";

interface GenieChannelStripProps {
  brandColor: string;
  secondaryColor?: string | null;
  /** The tenant's own contact number, from `agent.phone.number`. */
  phoneNumber?: string | null;
  /** The Genie's mailbox address, from `agent.mailbox?.email_address`. */
  emailAddress?: string | null;
  /** Same-origin path to the Genie Page, when it's live. */
  helpPageUrl?: string | null;
  /** Published guide count, when known — shown as the "Guides & manuals" detail line. */
  guideCount?: number;
  /**
   * "start" shows Call / Email / Guides & manuals with the section label.
   * "wrapup" swaps Guides & manuals for a "Talk again" action and drops the label.
   */
  variant: "start" | "wrapup";
  /** Fired when "Talk again" is chosen (wrapup variant only). */
  onTalkAgain?: () => void;
  /** Fired with the channel key whenever a chip is chosen — analytics only. */
  onSelect?: (channel: GenieChannelKey) => void;
}

interface ChannelChip {
  key: GenieChannelKey;
  label: string;
  detail?: string;
  genieIcon: GenieIconName;
  href?: string;
  onClick?: () => void;
}

/**
 * "Other ways to reach this Genie" — the same channel-strip idea as the public
 * Genie Page's `HubChannels`, scaled down to a few pill chips for the live-agent
 * surface. A channel with no data is simply omitted; the whole strip disappears
 * if none apply, so a Genie with no phone/mailbox/page still renders cleanly.
 *
 * Pure presentational — the caller owns which channels exist and what each
 * chip does.
 */
export const GenieChannelStrip = ({
  brandColor,
  secondaryColor,
  phoneNumber,
  emailAddress,
  helpPageUrl,
  guideCount,
  variant,
  onTalkAgain,
  onSelect,
}: GenieChannelStripProps) => {
  const end = secondaryColor || "#FF4DB8";
  const chips: ChannelChip[] = [];

  if (phoneNumber) {
    chips.push({
      key: "call",
      label: "Call us",
      detail: phoneNumber,
      genieIcon: "phone",
      href: `tel:${phoneNumber.replace(/[^\d+]/g, "")}`,
    });
  }
  if (emailAddress) {
    chips.push({
      key: "email",
      label: "Email",
      detail: "Reaches the same place",
      genieIcon: "mail",
      href: `mailto:${emailAddress}`,
    });
  }
  if (variant === "start" && helpPageUrl) {
    chips.push({
      key: "guides",
      label: "Guides & manuals",
      detail: typeof guideCount === "number" && guideCount > 0 ? `${guideCount} published` : undefined,
      genieIcon: "book-open",
      href: helpPageUrl,
    });
  }
  if (variant === "wrapup" && onTalkAgain) {
    chips.push({
      key: "talk_again",
      label: "Talk again",
      genieIcon: "sync",
      onClick: onTalkAgain,
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-col items-center">
      {variant === "start" && (
        <span className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-[#9AA7B6]">
          Other ways to reach this Genie
        </span>
      )}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {chips.map((chip) => {
          const body = (
            <>
              <span
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
                style={{ background: `linear-gradient(135deg, ${brandColor}1a, ${end}1a)` }}
              >
                <GenieIcon
                  name={chip.genieIcon}
                  size={16}
                  color={brandColor}
                  secondaryColor={end}
                  gradientEnabled
                />
              </span>
              <span className="flex min-w-0 flex-col items-start leading-tight">
                <span className="text-[12.5px] font-semibold text-[#222B45]">{chip.label}</span>
                {chip.detail && (
                  <span className="max-w-[140px] truncate text-[11px] text-[#7C8B9C]">{chip.detail}</span>
                )}
              </span>
            </>
          );
          const className =
            "group flex items-center gap-2 rounded-full border border-[#E9EDF3] bg-white px-3 py-1.5 text-left transition-all hover:-translate-y-[1px] hover:shadow-[0_8px_20px_-14px_rgba(15,23,42,0.35)]";
          const handleSelect = () => {
            if (chip.key === "call") trackCallClick("genie_channel_strip");
            onSelect?.(chip.key);
          };

          return chip.href ? (
            <a key={chip.key} href={chip.href} className={className} onClick={handleSelect}>
              {body}
            </a>
          ) : (
            <button
              key={chip.key}
              type="button"
              onClick={() => {
                handleSelect();
                chip.onClick?.();
              }}
              className={className}
            >
              {body}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default GenieChannelStrip;
